import React, { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EmailOutlined as EmailIcon,
  LocalShipping as ShippingIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { formatDate } from "../../utils";
import { useUserStore } from "../../store";

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: { xs: "12px", sm: "16px" },
    m: { xs: 2, sm: 4 },
    maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
  },
};

// Basket-import suppliers that identify the workshop by its login alone.
const SUPPLIERS_WITHOUT_CUSTOMER_ACCOUNT = ["meko", "ratparts"];

function SupplierConnectionsSection({ t }) {
  const { fetchUser, currentOrganization } = useUserStore();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgBusinessId, setOrgBusinessId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [formData, setFormData] = useState({
    supplier_type: "orum",
    username: "",
    password: "",
    customer_account: "",
    supplier_number: "",
    sftp_username: "",
    sftp_password: "",
    ovt_identifier: "",
    default_order_type: 1,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingIntegration, setDeletingIntegration] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isOrumClient, setIsOrumClient] = useState(false);
  const [sendingActivation, setSendingActivation] = useState(false);
  const [activationSent, setActivationSent] = useState(false);
  const [isAtoyClient, setIsAtoyClient] = useState(false);
  const [sendingSftpRequest, setSendingSftpRequest] = useState(false);
  const [sftpRequestSent, setSftpRequestSent] = useState(false);
  const [atoySalesRepEmail, setAtoySalesRepEmail] = useState("");
  const [atoySalesRepEmailError, setAtoySalesRepEmailError] = useState("");
  const [isMekoClient, setIsMekoClient] = useState(false);
  const [sendingMekoPasswordRequest, setSendingMekoPasswordRequest] = useState(false);
  const [mekoPasswordRequestSent, setMekoPasswordRequestSent] = useState(false);
  // Sähköpostitilaus: an "email" integration per vendor, switched on by setting the
  // vendor's order email. Saved through the vendor endpoint, not /api/suppliers/.
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailEditing, setEmailEditing] = useState(null);
  const [emailVendor, setEmailVendor] = useState(null);
  const [emailVendorInput, setEmailVendorInput] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [emailErrors, setEmailErrors] = useState({});
  const [emailSaving, setEmailSaving] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);

  const loadVendorOptions = async () => {
    try {
      const res = await axios.get("/api/vendors/?typeahead=true");
      const rows = Array.isArray(res.data) ? res.data : res.data.results || [];
      // Vendors already ordering through an API integration cannot also order by email.
      setVendorOptions(rows.filter((v) => !v.api_integration_type));
    } catch (error) {
      setVendorOptions([]);
    }
  };

  const handleOpenEmailAdd = () => {
    setEmailEditing(null);
    setEmailVendor(null);
    setEmailVendorInput("");
    setEmailAddress("");
    setEmailErrors({});
    setEmailDialogOpen(true);
    loadVendorOptions();
  };

  const handleOpenEmailEdit = (integration) => {
    setEmailEditing(integration);
    setEmailVendor({ id: integration.vendor_id, name: integration.vendor_name || integration.display_name });
    setEmailVendorInput(integration.vendor_name || integration.display_name);
    setEmailAddress(integration.order_email || "");
    setEmailErrors({});
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailEditing(null);
    setEmailErrors({});
  };

  const handleSaveEmail = async () => {
    const address = emailAddress.trim();
    const errs = {};
    const vendorName = emailVendorInput.trim();
    if (!emailEditing && !emailVendor && !vendorName) errs.vendor = t("supplierConnections.vendorRequired");
    if (!address) errs.order_email = t("supplierConnections.orderEmailRequired");
    if (Object.keys(errs).length) {
      setEmailErrors(errs);
      return;
    }
    setEmailSaving(true);
    setEmailErrors({});
    try {
      const vendorId = emailEditing ? emailEditing.vendor_id : emailVendor?.id;
      if (vendorId) {
        await axios.patch(`/api/vendors/${vendorId}/`, { order_email: address });
      } else {
        await axios.post("/api/vendors/", { name: vendorName, order_email: address });
      }
      setSnackbar({ open: true, message: t("supplierConnections.emailVendorSaved"), severity: "success" });
      handleCloseEmailDialog();
      fetchIntegrations();
      await fetchUser();
    } catch (error) {
      const data = error.response?.data || {};
      setEmailErrors({
        order_email: data.order_email?.[0],
        vendor: data.name?.[0],
        general: data.order_email || data.name ? undefined : data.error || data.detail || t("supplierConnections.saveFailed"),
      });
    } finally {
      setEmailSaving(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
    if (currentOrganization?.organization_id) {
      axios.get(`/api/organizations/${currentOrganization.organization_id}/`)
        .then((res) => setOrgBusinessId(res.data.business_id || ""))
        .catch(() => {});
    }
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get("/api/suppliers/");
      setIntegrations(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch supplier integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const deriveOvt = (businessId) =>
    businessId ? `0037${businessId.replace("-", "")}` : "";

  const blankFormData = (supplier_type = "orum") => ({
    supplier_type,
    username: "",
    password: "",
    customer_account: "",
    supplier_number: supplier_type === "atoy" ? "100" : "",
    sftp_username: "",
    sftp_password: "",
    ovt_identifier: supplier_type === "atoy" ? deriveOvt(orgBusinessId) : "",
    default_order_type: 1,
  });

  const handleOpenAdd = () => {
    setEditingIntegration(null);
    setFormData(blankFormData("orum"));
    setErrors({});
    setIsOrumClient(false);
    setSendingActivation(false);
    setActivationSent(false);
    setIsAtoyClient(false);
    setSftpRequestSent(false);
    setAtoySalesRepEmail("");
    setAtoySalesRepEmailError("");
    setIsMekoClient(false);
    setSendingMekoPasswordRequest(false);
    setMekoPasswordRequestSent(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (integration) => {
    setEditingIntegration(integration);
    const cfg = integration.config || {};
    setFormData({
      ...blankFormData(integration.supplier_type),
      customer_account: integration.customer_account || "",
      supplier_number: cfg.supplier_number || "",
      ovt_identifier: cfg.ovt_identifier || "",
      default_order_type: cfg.default_order_type || 1,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingIntegration(null);
    setErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "supplier_type") {
      setFormData((prev) => ({
        ...prev,
        supplier_type: value,
        supplier_number: value === "atoy" && !prev.supplier_number ? "100" : prev.supplier_number,
        ovt_identifier: value === "atoy" && !prev.ovt_identifier ? deriveOvt(orgBusinessId) : prev.ovt_identifier,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSendActivationEmail = async () => {
    setSendingActivation(true);
    try {
      await axios.post("/api/suppliers/request-activation/");
      setActivationSent(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("supplierConnections.activationEmailFailed"),
        severity: "error",
      });
    } finally {
      setSendingActivation(false);
    }
  };

  const handleRequestAtoySftp = async () => {
    const email = atoySalesRepEmail.trim();
    if (!email) {
      setAtoySalesRepEmailError(t("supplierConnections.atoySalesRepEmailRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAtoySalesRepEmailError(t("supplierConnections.atoySalesRepEmailInvalid"));
      return;
    }
    setAtoySalesRepEmailError("");
    setSendingSftpRequest(true);
    try {
      await axios.post("/api/suppliers/request-atoy-sftp/", { sales_rep_email: email });
      setSftpRequestSent(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("supplierConnections.atoySftpRequestFailed"),
        severity: "error",
      });
    } finally {
      setSendingSftpRequest(false);
    }
  };

  const handleRequestMekoPassword = async () => {
    setSendingMekoPasswordRequest(true);
    try {
      await axios.post("/api/suppliers/request-meko-password/");
      setMekoPasswordRequestSent(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("supplierConnections.mekoPasswordRequestFailed"),
        severity: "error",
      });
    } finally {
      setSendingMekoPasswordRequest(false);
    }
  };

  const buildAtoyConfig = (data) => {
    const cfg = {};
    if (data.supplier_number) cfg.supplier_number = data.supplier_number;
    if (data.ovt_identifier) cfg.ovt_identifier = data.ovt_identifier;
    if (data.default_order_type) cfg.default_order_type = parseInt(data.default_order_type, 10);
    return cfg;
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});

    try {
      if (editingIntegration) {
        const payload = {};
        if (formData.username) payload.username = formData.username;
        if (formData.password) payload.password = formData.password;
        if (formData.customer_account !== editingIntegration.customer_account) {
          payload.customer_account = formData.customer_account;
        }
        if (formData.supplier_type === "atoy") {
          payload.config = buildAtoyConfig(formData);
          if (formData.sftp_username) payload.sftp_username = formData.sftp_username;
          if (formData.sftp_password) payload.sftp_password = formData.sftp_password;
        }

        await axios.patch(`/api/suppliers/${editingIntegration.id}/`, payload);
        setSnackbar({ open: true, message: t("supplierConnections.updated"), severity: "success" });
      } else {
        const createPayload = {
          supplier_type: formData.supplier_type,
          username: formData.username,
          password: formData.password,
          // A hidden field must not send whatever was typed before the supplier was switched.
          customer_account: SUPPLIERS_WITHOUT_CUSTOMER_ACCOUNT.includes(formData.supplier_type)
            ? ""
            : formData.customer_account,
        };
        if (formData.supplier_type === "atoy") {
          createPayload.config = buildAtoyConfig(formData);
          if (formData.sftp_username) createPayload.sftp_username = formData.sftp_username;
          if (formData.sftp_password) createPayload.sftp_password = formData.sftp_password;
        }
        await axios.post("/api/suppliers/", createPayload);
        setSnackbar({ open: true, message: t("supplierConnections.created"), severity: "success" });
        // Refresh user store so menu updates with has_supplier_integrations
        await fetchUser();
      }

      handleCloseDialog();
      fetchIntegrations();
    } catch (error) {
      const data = error.response?.data;
      if (data?.error_code === "auth_failed") {
        setErrors({
          general: formData.supplier_type === "ratparts"
            ? t("supplierConnections.ratpartsAuthFailedHint")
            : t("supplierConnections.authFailedHint"),
        });
      } else if (data?.error) {
        setErrors({ general: data.error });
      } else if (data?.supplier_type?.[0]) {
        setErrors({ general: data.supplier_type[0] });
      } else if (data?.non_field_errors?.[0]) {
        setErrors({ general: data.non_field_errors[0] });
      } else {
        setSnackbar({ open: true, message: t("supplierConnections.saveFailed"), severity: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (integration) => {
    setDeletingIntegration(integration);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/suppliers/${deletingIntegration.id}/`);
      setDeleteDialogOpen(false);
      setDeletingIntegration(null);
      setSnackbar({ open: true, message: t("supplierConnections.deleted"), severity: "success" });
      fetchIntegrations();
      // Refresh user store so menu updates with has_supplier_integrations
      await fetchUser();
    } catch (error) {
      setSnackbar({ open: true, message: t("supplierConnections.deleteFailed"), severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const isEditing = !!editingIntegration;

  return (
    <>
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <ShippingIcon />
          </Box>
        </Box>
        <Typography variant="h6" gutterBottom>
          {t("supplierConnections.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("supplierConnections.description")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : integrations.length === 0 ? (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              {t("supplierConnections.addConnection")}
            </Button>
            <Button variant="outlined" startIcon={<EmailIcon />} onClick={handleOpenEmailAdd} data-testid="add-email-vendor">
              {t("supplierConnections.addEmailVendor")}
            </Button>
          </Box>
        ) : (
          <Box>
            {integrations.map((integration) => (
              <Box
                key={integration.id}
                data-testid={`supplier-integration-${integration.supplier_type}`}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {integration.display_name}
                  </Typography>
                  <Chip
                    label={integration.supplier_type === "email"
                      ? t("supplierConnections.emailOrdering")
                      : t("supplierConnections.connected")}
                    color={integration.supplier_type === "email" ? "info" : "success"}
                    size="small"
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                </Box>
                {integration.supplier_type === "email" && integration.order_email && (
                  <Typography variant="body2" color="text.secondary" data-testid="email-vendor-address">
                    {t("supplierConnections.ordersGoTo")}: {integration.order_email}
                  </Typography>
                )}
                {integration.customer_account && (
                  <Typography variant="body2" color="text.secondary">
                    {t("supplierConnections.customerAccount")}: {integration.customer_account}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t("supplierConnections.connectedDate")}: {formatDate(integration.created_at)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => (integration.supplier_type === "email"
                      ? handleOpenEmailEdit(integration)
                      : handleOpenEdit(integration))}
                  >
                    {t("Edit")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleOpenDelete(integration)}
                  >
                    {t("Delete")}
                  </Button>
                </Box>
              </Box>
            ))}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                {t("supplierConnections.addConnection")}
              </Button>
              <Button variant="outlined" size="small" startIcon={<EmailIcon />} onClick={handleOpenEmailAdd} data-testid="add-email-vendor">
                {t("supplierConnections.addEmailVendor")}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditing ? t("supplierConnections.editConnection") : t("supplierConnections.addConnection")}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              select
              label={t("supplierConnections.supplier")}
              name="supplier_type"
              value={formData.supplier_type}
              onChange={handleFormChange}
              disabled={isEditing}
              fullWidth
              sx={textFieldStyles}
            >
              <MenuItem value="orum">Örum</MenuItem>
              <MenuItem value="atoy">Atoy</MenuItem>
              <MenuItem value="meko">MEKO</MenuItem>
              <MenuItem value="ratparts">Rengastien Autotarvike</MenuItem>
            </TextField>
            {!isEditing && formData.supplier_type === "orum" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Alert severity="info" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                  {t("supplierConnections.orumSetupInfo")}
                </Alert>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isOrumClient}
                      onChange={(e) => setIsOrumClient(e.target.checked)}
                    />
                  }
                  label={t("supplierConnections.orumClientConfirm")}
                />
                {isOrumClient && !activationSent && (
                  <Button
                    variant="outlined"
                    onClick={handleSendActivationEmail}
                    disabled={sendingActivation}
                    startIcon={sendingActivation ? <CircularProgress size={16} /> : <SendIcon />}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {t("supplierConnections.sendActivationRequest")}
                  </Button>
                )}
                {activationSent && (
                  <Alert severity="success">
                    {t("supplierConnections.activationEmailSent")}
                  </Alert>
                )}
              </Box>
            )}
            {formData.supplier_type === "meko" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Alert severity="info" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                  {t("supplierConnections.mekoSetupInfo")}
                </Alert>
                {!isEditing && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isMekoClient}
                          onChange={(e) => setIsMekoClient(e.target.checked)}
                        />
                      }
                      label={t("supplierConnections.mekoClientConfirm")}
                    />
                    {isMekoClient && !mekoPasswordRequestSent && (
                      <Button
                        variant="outlined"
                        onClick={handleRequestMekoPassword}
                        disabled={sendingMekoPasswordRequest}
                        startIcon={sendingMekoPasswordRequest ? <CircularProgress size={16} /> : <SendIcon />}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {t("supplierConnections.mekoPasswordRequestButton")}
                      </Button>
                    )}
                    {mekoPasswordRequestSent && (
                      <Alert severity="success">
                        {t("supplierConnections.mekoPasswordRequestSent")}
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}
            {formData.supplier_type === "ratparts" && (
              <Alert severity="info" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                {t("supplierConnections.ratpartsSetupInfo")}
              </Alert>
            )}
            <TextField
              label={formData.supplier_type === "meko"
                ? t("supplierConnections.mekoLogin")
                : t("supplierConnections.username")}
              name="username"
              value={formData.username}
              onChange={handleFormChange}
              fullWidth
              sx={textFieldStyles}
              placeholder={isEditing ? t("supplierConnections.keepCredentials") : ""}
              autoComplete="off"
            />
            <TextField
              label={formData.supplier_type === "meko"
                ? t("supplierConnections.mekoPassword")
                : t("supplierConnections.password")}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleFormChange}
              fullWidth
              sx={textFieldStyles}
              placeholder={isEditing ? t("supplierConnections.keepCredentials") : ""}
              autoComplete="new-password"
            />
            {!SUPPLIERS_WITHOUT_CUSTOMER_ACCOUNT.includes(formData.supplier_type) && (
              <TextField
                label={t("supplierConnections.customerAccount")}
                name="customer_account"
                value={formData.customer_account}
                onChange={handleFormChange}
                fullWidth
                sx={textFieldStyles}
                placeholder={
                  formData.supplier_type === "atoy" ? "esim. 088810"
                  : t("supplierConnections.customerAccountPlaceholder")
                }
              />
            )}
            {formData.supplier_type === "atoy" && (
              <>
                <TextField
                  label={t("supplierConnections.atoySupplierNumber")}
                  name="supplier_number"
                  value={formData.supplier_number}
                  onChange={handleFormChange}
                  fullWidth
                  sx={textFieldStyles}
                  placeholder="100"
                  helperText={t("supplierConnections.atoySupplierNumberHelp")}
                />
                <TextField
                  label={t("supplierConnections.atoyOvtIdentifier")}
                  name="ovt_identifier"
                  value={formData.ovt_identifier}
                  onChange={handleFormChange}
                  fullWidth
                  sx={textFieldStyles}
                  placeholder="003704166999012"
                  helperText={t("supplierConnections.atoyOvtIdentifierHelp")}
                />
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("supplierConnections.atoySftpSection")}
                  </Typography>
                </Divider>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Alert severity="info" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                    {t("supplierConnections.atoySftpRequestInfo")}
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAtoyClient}
                        onChange={(e) => setIsAtoyClient(e.target.checked)}
                      />
                    }
                    label={t("supplierConnections.atoyClientConfirm")}
                  />
                  {isAtoyClient && !sftpRequestSent && (
                    <>
                      <TextField
                        label={t("supplierConnections.atoySalesRepEmail")}
                        value={atoySalesRepEmail}
                        onChange={(e) => {
                          setAtoySalesRepEmail(e.target.value);
                          if (atoySalesRepEmailError) setAtoySalesRepEmailError("");
                        }}
                        fullWidth
                        sx={textFieldStyles}
                        placeholder="etunimi.sukunimi@atoy.fi"
                        helperText={atoySalesRepEmailError || t("supplierConnections.atoySalesRepEmailHelp")}
                        error={!!atoySalesRepEmailError}
                        type="email"
                      />
                      <Button
                        variant="outlined"
                        onClick={handleRequestAtoySftp}
                        disabled={sendingSftpRequest}
                        startIcon={sendingSftpRequest ? <CircularProgress size={16} /> : <SendIcon />}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {t("supplierConnections.atoySftpRequestButton")}
                      </Button>
                    </>
                  )}
                  {sftpRequestSent && (
                    <Alert severity="success">
                      {t("supplierConnections.atoySftpRequestSent")}
                    </Alert>
                  )}
                </Box>
                <TextField
                  label={t("supplierConnections.atoySftpUsername")}
                  name="sftp_username"
                  value={formData.sftp_username}
                  onChange={handleFormChange}
                  fullWidth
                  sx={textFieldStyles}
                  placeholder={isEditing ? t("supplierConnections.keepCredentials") : ""}
                  helperText={t("supplierConnections.atoySftpUsernameHelp")}
                />
                <TextField
                  label={t("supplierConnections.atoySftpPassword")}
                  name="sftp_password"
                  type="password"
                  value={formData.sftp_password}
                  onChange={handleFormChange}
                  fullWidth
                  sx={textFieldStyles}
                  placeholder={isEditing ? t("supplierConnections.keepCredentials") : ""}
                />
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("supplierConnections.atoyOrderDefaultsSection")}
                  </Typography>
                </Divider>
                <TextField
                  select
                  label={t("supplierConnections.atoyDefaultOrderType")}
                  name="default_order_type"
                  value={formData.default_order_type}
                  onChange={handleFormChange}
                  fullWidth
                  sx={textFieldStyles}
                >
                  <MenuItem value={1}>1 — AL Päivätilaus</MenuItem>
                  <MenuItem value={2}>2 — FV Viikkotilaus</MenuItem>
                  <MenuItem value={3}>3 — FK Kausitilaus</MenuItem>
                  <MenuItem value={4}>4 — OS Osaset</MenuItem>
                  <MenuItem value={8}>8 — MO Motonet</MenuItem>
                  <MenuItem value={9}>9 — FV Jälkitoimitus sallittu</MenuItem>
                </TextField>
              </>
            )}
            {errors.general && (
              <Alert severity="error">{errors.general}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving
              ? t("supplierConnections.save") + "..."
              : isEditing
                ? t("supplierConnections.save")
                : t("supplierConnections.testAndSave")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email ordering (sähköpostitilaus) dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
        data-testid="email-vendor-dialog"
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("supplierConnections.emailVendorDialogTitle")}
          </Typography>
          <IconButton onClick={handleCloseEmailDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Alert severity="info" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
              {t("supplierConnections.emailVendorInfo")}
            </Alert>
            <Autocomplete
              freeSolo
              options={vendorOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={emailVendor}
              inputValue={emailVendorInput}
              onChange={(e, value) => {
                if (typeof value === "string") {
                  setEmailVendor(null);
                  setEmailVendorInput(value);
                } else {
                  setEmailVendor(value);
                  if (value && value.order_email && !emailAddress) setEmailAddress(value.order_email);
                }
              }}
              onInputChange={(e, value) => setEmailVendorInput(value)}
              disabled={!!emailEditing}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("supplierConnections.vendor")}
                  placeholder={t("supplierConnections.vendorPlaceholder")}
                  error={!!emailErrors.vendor}
                  helperText={emailErrors.vendor}
                  sx={textFieldStyles}
                  inputProps={{ ...params.inputProps, "data-testid": "email-vendor-name" }}
                />
              )}
            />
            <TextField
              label={t("supplierConnections.orderEmail")}
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              fullWidth
              sx={textFieldStyles}
              error={!!emailErrors.order_email}
              helperText={emailErrors.order_email || t("vendors.orderEmailHelp")}
              inputProps={{ "data-testid": "email-vendor-order-email" }}
            />
            {emailErrors.general && <Alert severity="error">{emailErrors.general}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEmailDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSaveEmail}
            disabled={emailSaving}
            startIcon={emailSaving ? <CircularProgress size={16} /> : null}
            data-testid="email-vendor-save"
          >
            {t("supplierConnections.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("supplierConnections.deleteConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("supplierConnections.deleteConfirmMessage", {
              name: deletingIntegration?.display_name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withTranslation()(SupplierConnectionsSection);
